// sessionStorage marker recording that this tab has a live interview in flight.
// Used to auto-resume shared-link interviews after a page refresh: the server
// cannot identify a shared-link visitor, so the tab must remember which call it
// was in. sessionStorage is deliberate: it survives refresh but is tab-scoped,
// so the next candidate on a shared kiosk device never inherits a stale resume
// target. Individual links resume server-side (resume_in_flight_available) and
// do not need this marker.
//
// The marker only triggers a resume attempt; the server's recency/status check
// at token fetch time is the authoritative gate.

import { safeJsonParse } from 'utils/json';
import { SessionStorageKey, sessionStore } from 'utils/typedStorage';
import {
  isFiniteNumber,
  isNonEmptyString,
  isRecord,
  isString,
} from 'utils/typeGuards';

// Hygiene bound only; the server ages out stale CONNECTED calls after 90 minutes.
const MARKER_TTL_MS = 3 * 60 * 60 * 1000;

export interface InFlightInterviewMarker {
  callId: string;
  campaignId: string;
  candidateId: string;
  candidateName: string;
}

interface StoredMarker extends InFlightInterviewMarker {
  ts: number;
}

export function setInFlightMarker(marker: InFlightInterviewMarker): void {
  // When storage is blocked the marker lives in memory, so a refresh won't fast-path.
  const stored: StoredMarker = { ...marker, ts: Date.now() };
  sessionStore.set(
    SessionStorageKey.InFlightInterviewMarker,
    JSON.stringify(stored)
  );
}

export function getInFlightMarker(): InFlightInterviewMarker | null {
  const raw = sessionStore.get(SessionStorageKey.InFlightInterviewMarker);
  if (!raw) {
    return null;
  }
  const stored = safeJsonParse(raw);
  if (
    !isRecord(stored) ||
    !isNonEmptyString(stored.callId) ||
    !isNonEmptyString(stored.campaignId) ||
    !isString(stored.candidateId) ||
    !isString(stored.candidateName) ||
    !isFiniteNumber(stored.ts) ||
    Date.now() - stored.ts > MARKER_TTL_MS
  ) {
    clearInFlightMarker();
    return null;
  }
  return {
    callId: stored.callId,
    campaignId: stored.campaignId,
    candidateId: stored.candidateId,
    candidateName: stored.candidateName,
  };
}

export function clearInFlightMarker(): void {
  sessionStore.remove(SessionStorageKey.InFlightInterviewMarker);
}
